/**
 * 고객설문 운영 템플릿 — sections + questionBank 일괄보내기/가져오기
 */

const TXT_MAGIC = '# SGEZ 고객설문 문항 v1'

function deepClone(value) {
  try {
    return JSON.parse(JSON.stringify(value == null ? {} : value))
  } catch {
    return {}
  }
}

export function parseSchemaSafe(text) {
  try {
    return JSON.parse(String(text || '{}'))
  } catch {
    return null
  }
}

function escapeTxtValue(v) {
  return String(v ?? '')
    .replace(/\\/g, '\\\\')
    .replace(/\r\n/g, '\n')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\n')
}

function unescapeTxtValue(v) {
  return String(v ?? '')
    .replace(/\\n/g, '\n')
    .replace(/\\\\/g, '\\')
}

function parseKeyValueLine(line) {
  const idx = line.indexOf('=')
  if (idx <= 0) return null
  const key = line.slice(0, idx).trim().toLowerCase()
  const rawVal = line.slice(idx + 1)
  return { key, value: unescapeTxtValue(rawVal) }
}

function normalizeOption(opt) {
  if (typeof opt === 'string') {
    return { value: opt, label: opt }
  }
  const v = opt?.value != null ? String(opt.value) : ''
  const l = opt?.label != null ? String(opt.label) : v
  return { value: v || l, label: l || v }
}

const OPTION_TYPES = ['single-select', 'multi-select', 'select']

function isOptionType(type) {
  return OPTION_TYPES.includes(String(type || '').toLowerCase())
}

export function normalizeImportedQuestion(raw) {
  const q = raw && typeof raw === 'object' ? raw : {}
  const type = String(q.type || 'text').toLowerCase()
  const opts = Array.isArray(q.options) ? q.options.map(normalizeOption) : []
  const out = {
    key: String(q.key || '').trim(),
    type,
    label: String(q.label || ''),
    required: q.required === true,
  }
  if (q.placeholder != null && String(q.placeholder).trim()) out.placeholder = String(q.placeholder)
  if (q.helpText != null && String(q.helpText).trim()) out.helpText = String(q.helpText)
  if (isOptionType(type)) {
    const filtered = opts.filter((o) => (o.value || o.label))
    out.options = filtered.length > 0 ? filtered : [
      { value: '보기1', label: '보기1' },
      { value: '보기2', label: '보기2' },
    ]
  }
  return out
}

function formatQuestionTxt(q) {
  const n = normalizeImportedQuestion(q)
  let s = `key=${escapeTxtValue(n.key)}\n`
  s += `type=${escapeTxtValue(n.type)}\n`
  s += `label=${escapeTxtValue(n.label)}\n`
  s += `required=${n.required ? 'true' : 'false'}\n`
  if (n.placeholder) s += `placeholder=${escapeTxtValue(n.placeholder)}\n`
  if (n.helpText) s += `helpText=${escapeTxtValue(n.helpText)}\n`
  if (isOptionType(n.type) && Array.isArray(n.options)) {
    for (const o of n.options) {
      const { value, label } = normalizeOption(o)
      const line = value && label && value !== label ? `${value}|${label}` : (label || value || '')
      if (line) s += `option=${escapeTxtValue(line)}\n`
    }
  }
  return s
}

function parseQuestionBlock(lines, startIdx) {
  const q = { options: [] }
  let i = startIdx
  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (trimmed === '[QUESTION]' || trimmed === '[SECTION]' || trimmed === '[BANK_ITEM]') break
    const kv = parseKeyValueLine(lines[i])
    if (kv) {
      if (kv.key === 'option') {
        const pipe = kv.value.indexOf('|')
        if (pipe > 0) {
          const value = kv.value.slice(0, pipe).trim()
          const label = kv.value.slice(pipe + 1).trim()
          q.options.push({ value: value || label, label: label || value })
        } else {
          q.options.push({ value: kv.value, label: kv.value })
        }
      } else if (kv.key === 'required') {
        q.required = kv.value === 'true' || kv.value === 'yes' || kv.value === '1'
      } else {
        q[kv.key] = kv.value
      }
    }
    i += 1
  }
  return { question: normalizeImportedQuestion(q), nextIdx: i }
}

/**
 * 현재 편집기 상태(스키마 텍스트 + questionBank 엔트리)에서보낼 payload 생성
 */
export function buildExportPayload(schemaText, questionBankEntries) {
  const s = parseSchemaSafe(schemaText)
  if (!s || typeof s !== 'object') {
    throw new Error('스키마 JSON이 비어 있거나 형식이 올바르지 않습니다.')
  }
  const sections = Array.isArray(s.sections) ? deepClone(s.sections) : []
  const bank = Array.isArray(questionBankEntries)
    ? questionBankEntries
      .map((e, i) => normalizeBankEntryForExport(e, i))
      .filter(Boolean)
    : []
  return { sections, questionBank: bank }
}

function normalizeBankEntryForExport(raw, rowIndex = 0) {
  if (!raw || typeof raw !== 'object') return null
  const q = raw.question && typeof raw.question === 'object' ? deepClone(raw.question) : {}
  if (!String(q.key || '').trim()) {
    q.key = `qb_row_${rowIndex}_${Date.now().toString(36)}`
  }
  return {
    sectionKey: raw.sectionKey != null ? String(raw.sectionKey) : '',
    sectionTitle: raw.sectionTitle != null ? String(raw.sectionTitle) : '',
    question: normalizeImportedQuestion(q),
  }
}

export function serializeQuestionsJson(payload) {
  const body = {
    exportVersion: 1,
    kind: 'counsel-survey-questions',
    sections: payload.sections || [],
    questionBank: payload.questionBank || [],
  }
  return `${JSON.stringify(body, null, 2)}\n`
}

export function parseQuestionsJson(text) {
  let data
  try {
    data = JSON.parse(String(text || '').replace(/^\uFEFF/, ''))
  } catch (e) {
    throw new Error('JSON 파싱에 실패했습니다.')
  }
  if (!data || typeof data !== 'object') throw new Error('JSON 루트가 객체가 아닙니다.')
  let sections = data.sections
  let questionBank = data.questionBank
  if (!Array.isArray(sections) && data.schema && typeof data.schema === 'object') {
    sections = data.schema.sections
    questionBank = data.schema.questionBank
  }
  if (!Array.isArray(sections)) sections = []
  if (!Array.isArray(questionBank)) questionBank = []
  if (sections.length === 0 && questionBank.length === 0) {
    throw new Error('JSON에 sections 또는 questionBank 내용이 없습니다.')
  }
  return validateAndNormalizePayload({ sections, questionBank })
}

export function serializeQuestionsTxt(payload, templateName = '') {
  const name = String(templateName || '').replace(/\r?\n/g, ' ')
  let out = `${TXT_MAGIC}\n`
  out += `# 템플릿: ${name}\n\n`
  const sections = payload.sections || []
  for (const sec of sections) {
    const sk = sec?.key != null ? String(sec.key) : ''
    const st = sec?.title != null ? String(sec.title) : ''
    out += `[SECTION]\nkey=${escapeTxtValue(sk)}\ntitle=${escapeTxtValue(st)}\n\n`
    const qs = Array.isArray(sec?.questions) ? sec.questions : []
    for (const q of qs) {
      out += `[QUESTION]\n${formatQuestionTxt(q)}\n`
    }
  }
  const bank = payload.questionBank || []
  for (const row of bank) {
    out += `[BANK_ITEM]\n`
    out += `sectionKey=${escapeTxtValue(row.sectionKey != null ? String(row.sectionKey) : '')}\n`
    out += `sectionTitle=${escapeTxtValue(row.sectionTitle != null ? String(row.sectionTitle) : '')}\n`
    out += formatQuestionTxt(row.question || {})
    out += '\n'
  }
  return out
}

export function parseQuestionsTxt(text) {
  const raw = String(text || '').replace(/^\uFEFF/, '')
  if (!raw.includes('[SECTION]') && !raw.includes('[BANK_ITEM]')) {
    throw new Error('TXT 형식이 아닙니다. ([SECTION] 또는 [BANK_ITEM] 블록이 필요합니다.)')
  }
  const lines = raw.split(/\r?\n/)
  const sectionsOut = []
  const bankOut = []
  let currentSection = null
  let i = 0

  const pushSection = () => {
    if (!currentSection) return
    const hasKey = String(currentSection.key || '').trim()
    const hasQs = Array.isArray(currentSection.questions) && currentSection.questions.length > 0
    if (hasKey || hasQs) {
      if (!hasKey) {
        currentSection.key = `section_${sectionsOut.length}`
      }
      if (!String(currentSection.title || '').trim()) {
        currentSection.title = currentSection.key
      }
      sectionsOut.push(currentSection)
    }
    currentSection = null
  }

  while (i < lines.length) {
    const trimmed = lines[i].trim()
    if (trimmed === '[SECTION]') {
      pushSection()
      currentSection = { key: '', title: '', questions: [] }
      i += 1
      while (i < lines.length) {
        const t2 = lines[i].trim()
        if (t2 === '[SECTION]' || t2 === '[QUESTION]' || t2 === '[BANK_ITEM]') break
        const kv = parseKeyValueLine(lines[i])
        if (kv) {
          if (kv.key === 'key') currentSection.key = kv.value
          if (kv.key === 'title') currentSection.title = kv.value
        }
        i += 1
      }
      continue
    }
    if (trimmed === '[QUESTION]') {
      if (!currentSection) {
        throw new Error('[QUESTION] 이전에 [SECTION] 이 필요합니다.')
      }
      i += 1
      const { question, nextIdx } = parseQuestionBlock(lines, i)
      if (!String(question.key || '').trim()) {
        throw new Error('문항 key가 비어 있습니다.')
      }
      currentSection.questions.push(question)
      i = nextIdx
      continue
    }
    if (trimmed === '[BANK_ITEM]') {
      pushSection()
      i += 1
      const { question, nextIdx } = parseQuestionBlock(lines, i)
      if (!String(question.key || '').trim()) {
        throw new Error('추천 문항(BANK)의 key가 비어 있습니다.')
      }
      const meta = { sectionKey: '', sectionTitle: '' }
      let j = i
      while (j < nextIdx) {
        const kv = parseKeyValueLine(lines[j])
        if (kv) {
          if (kv.key === 'sectionkey') meta.sectionKey = kv.value
          if (kv.key === 'sectiontitle') meta.sectionTitle = kv.value
        }
        j += 1
      }
      bankOut.push({
        sectionKey: meta.sectionKey,
        sectionTitle: meta.sectionTitle,
        question,
      })
      i = nextIdx
      continue
    }
    i += 1
  }
  pushSection()

  if (sectionsOut.length === 0 && bankOut.length === 0) {
    throw new Error('가져올 섹션 또는 추천 문항이 없습니다.')
  }

  return validateAndNormalizePayload({ sections: sectionsOut, questionBank: bankOut })
}

export function validateAndNormalizePayload({ sections, questionBank }) {
  const secIn = Array.isArray(sections) ? sections : []
  const bankIn = Array.isArray(questionBank) ? questionBank : []
  if (secIn.length === 0 && bankIn.length === 0) {
    throw new Error('sections와 questionBank가 모두 비어 있습니다.')
  }

  const normSections = secIn.map((sec, si) => {
    const key = String(sec?.key ?? `section_${si}`).trim() || `section_${si}`
    const title = sec?.title != null ? String(sec.title) : key
    const qs = Array.isArray(sec?.questions) ? sec.questions : []
    return {
      key,
      title,
      questions: qs.map((q) => normalizeImportedQuestion(q)),
    }
  })

  const normBank = bankIn.map((row, bi) => ({
    sectionKey: row?.sectionKey != null ? String(row.sectionKey) : '',
    sectionTitle: row?.sectionTitle != null ? String(row.sectionTitle) : '',
    question: normalizeImportedQuestion(row?.question || row),
  }))

  const seen = new Set()
  const dup = []
  const register = (k) => {
    const id = String(k || '').trim()
    if (!id) return
    if (seen.has(id)) dup.push(id)
    seen.add(id)
  }
  for (const sec of normSections) {
    for (const q of sec.questions || []) register(q.key)
  }
  for (const row of normBank) register(row.question?.key)
  if (dup.length) {
    throw new Error(`문항 ID(key)가 중복되었습니다: ${[...new Set(dup)].join(', ')}`)
  }

  for (const sec of normSections) {
    for (const q of sec.questions || []) {
      if (isOptionType(q.type)) {
        const ok = Array.isArray(q.options) && q.options.some((o) => normalizeOption(o).value || normalizeOption(o).label)
        if (!ok) throw new Error(`선택형 문항 "${q.key}" 에 보기(option)가 필요합니다.`)
      }
    }
  }
  for (const row of normBank) {
    const q = row.question
    if (isOptionType(q.type)) {
      const ok = Array.isArray(q.options) && q.options.some((o) => normalizeOption(o).value || normalizeOption(o).label)
      if (!ok) throw new Error(`추천 문항 "${q.key}" 에 보기(option)가 필요합니다.`)
    }
  }

  return { sections: normSections, questionBank: normBank }
}

export function applyPayloadToSchemaText(schemaText, payload) {
  const base = parseSchemaSafe(schemaText)
  if (!base || typeof base !== 'object') {
    throw new Error('현재 스키마 JSON을 해석할 수 없습니다.')
  }
  const next = deepClone(base)
  next.sections = deepClone(payload.sections)
  next.questionBank = deepClone(payload.questionBank || [])
  return JSON.stringify(next, null, 2)
}
